import { useMutation, useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  ImageOff,
  RotateCcw,
  Search,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { api, type CatalogueProduct, type ListingReport } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 350;

type Filter = "all" | "published" | "unpublished" | "blocked";

// Named after what they actually narrow. The Published column is read from the
// store on every page load, but these are database predicates over
// shopify_handle, which only records that a sync once happened: a product can
// have been synced and no longer be in the store. Calling them "In Shopify"
// would promise the column's answer and give a different one.
const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: "all", label: "All" },
  { value: "published", label: "Synced at least once" },
  { value: "unpublished", label: "Never synced" },
  { value: "blocked", label: "No description" },
];

// Icon + text on every badge, never colour alone (accessibility rule).
// Two answers only: Yes is a listing a shopper can reach on the Online Store,
// and everything else is No. A listing the store holds off the Online Store is
// a No that says why in its title, not a third badge.
const LISTING_BADGES: Record<
  ListingReport,
  { label: string; title: string; icon: typeof CheckCircle2; variant: "success" | "outline" }
> = {
  live: {
    label: "Yes",
    title: "In the store and published to the Online Store",
    icon: CheckCircle2,
    variant: "success",
  },
  unpublished: {
    label: "No",
    title: "The store has this listing but it is not on the Online Store, so nobody can buy it",
    icon: CircleDashed,
    variant: "outline",
  },
  absent: {
    label: "No",
    title: "The store has no listing at this handle",
    icon: CircleDashed,
    variant: "outline",
  },
  unknown: {
    label: "?",
    title: "The store could not be asked, so this is not an answer either way",
    icon: CircleDashed,
    variant: "outline",
  },
};

// How many columns the offers row has to span.
const COLUMN_COUNT = 5;

export function ProductsPage() {
  const searchId = useId();
  const filterId = useId();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [offset, setOffset] = useState(0);

  // The catalogue is ~480k rows, so every keystroke must not become a query.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Any change to the filters puts us back on the first page; staying on page 7
  // of a different result set shows an empty table for no reason.
  useEffect(() => {
    setOffset(0);
  }, [debounced, filter]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["catalogue", debounced, filter, offset],
    queryFn: () =>
      api.catalogue({
        search: debounced || undefined,
        onlyBlocked: filter === "blocked",
        published:
          filter === "published" ? true : filter === "unpublished" ? false : undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  });

  const products = data?.products ?? [];
  const total = data?.total ?? 0;
  const isFiltered = debounced !== "" || filter !== "all";

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div className="space-y-1.5">
          <CardTitle>All products</CardTitle>
          <CardDescription>
            The full catalogue. A product is identified by brand and reference: there is
            no name column, and that pair is also its Shopify title.
          </CardDescription>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor={searchId}>Search</Label>
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                id={searchId}
                type="search"
                className="pl-9"
                placeholder="ID, SKU, reference, brand or handle"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5 sm:w-72">
            <Label htmlFor={filterId}>Status</Label>
            <Select value={filter} onValueChange={(v) => setFilter(v as Filter)}>
              <SelectTrigger id={filterId}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTERS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isFiltered && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setFilter("all");
              }}
            >
              <RotateCcw className="size-4" />
              Clear
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">
            Could not load the catalogue
            {error instanceof Error ? `: ${error.message}` : "."}
          </p>
        ) : products.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {isFiltered
              ? "No product matches the search."
              : "The catalogue is empty."}
          </p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground" role="status">
              {total.toLocaleString("en-GB")} products
              {isFiltered ? " match" : ""} · showing{" "}
              {(offset + 1).toLocaleString("en-GB")}–
              {Math.min(offset + PAGE_SIZE, total).toLocaleString("en-GB")}
            </p>
            {/* The column is only worth reading when the store answered. Said
                here rather than per row: it is one failure, not twenty-five. */}
            {data?.shopifyError && (
              <p className="flex items-start gap-1.5 text-xs text-destructive" role="status">
                <TriangleAlert aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
                <span>
                  Shopify could not be asked what it publishes, so the Published column
                  is not an answer on this page: {data.shopifyError}
                </span>
              </p>
            )}
            {/* No overflow wrapper on purpose: the whole table has to fit the
                screen. Secondary attributes are stacked inside the cell they
                belong to (id and SKU under the reference) instead of each
                claiming a column of its own, and the per-supplier prices live
                in the row's own detail. */}
            <table className="w-full table-fixed caption-bottom text-sm">
              <caption className="sr-only">
                Catalogue products, most recently modified first. Open a row for the
                prices behind it.
              </caption>
              <colgroup>
                <col className="w-[27%]" />
                <col className="w-[25%]" />
                <col className="w-[24%]" />
                <col className="w-[15%]" />
                <col className="w-[9%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th scope="col" className="pb-2 pr-3 font-medium">Product</th>
                  <th scope="col" className="pb-2 pr-3 font-medium">Shopify handle</th>
                  <th scope="col" className="pb-2 pr-3 font-medium">Brand</th>
                  <th scope="col" className="pb-2 pr-3 font-medium">Published</th>
                  <th scope="col" className="pb-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <ProductRow key={product.id} product={product} />
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ProductRow({ product }: { product: CatalogueProduct }) {
  const sync = useMutation({
    mutationFn: () => api.triggerSync(product.id),
    onSuccess: (result) => {
      if (result.outcome === "already-running") {
        toast.info(`${product.reference}: already queued`);
      } else {
        toast.success(`${product.reference}: queued for Shopify`);
      }
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? `Could not queue: ${error.message}`
          : "Could not queue",
      );
    },
  });

  // Read from the store itself, not from anything stored here.
  const badge = LISTING_BADGES[product.listing];
  const BadgeIcon = badge.icon;
  const blocker = publishBlocker(product);
  const offersId = useId();
  const [expanded, setExpanded] = useState(false);

  // Guarded against a selection: dragging across a cell to copy a reference
  // ends in a click on the row, which must not also open it.
  const toggle = () => {
    if (window.getSelection()?.isCollapsed === false) return;
    setExpanded((open) => !open);
  };

  return (
    <>
    <tr
      className="cursor-pointer border-b border-border align-top last:border-0 hover:bg-muted/50"
      aria-expanded={expanded}
      aria-controls={offersId}
      onClick={toggle}
    >
      <td className="py-2 pr-3">
        <span className="block truncate font-medium" title={product.reference}>
          {product.reference}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          #{product.id}
          {product.sku !== product.reference && ` · ${product.sku}`}
        </span>
      </td>
      <td className="py-2 pr-3 text-muted-foreground">
        <span className="block truncate" title={product.shopifyHandle ?? undefined}>
          {product.shopifyHandle ?? "—"}
        </span>
      </td>
      <td className="py-2 pr-3">
        <span className="block truncate" title={product.brandName ?? undefined}>
          {product.brandName ?? "—"}
        </span>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={badge.variant} className="gap-1.5" title={badge.title}>
          <BadgeIcon className="size-3.5 shrink-0" />
          {badge.label}
        </Badge>
        {/* Only where the store has nothing, which is where it is the difference
            between a product waiting its turn and one the sync would refuse. */}
        {product.listing === "absent" && blocker !== null && (
          <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <TriangleAlert className="size-3 shrink-0" />
            {blocker}
          </span>
        )}
        {/* Not a blocker: a product with no image of its own is published with
            the default one, and that is where most of the catalogue lands. Shown
            anyway so the difference stays visible from here. */}
        {!product.hasImage && (
          <span className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <ImageOff className="size-3 shrink-0" />
            default image
          </span>
        )}
      </td>
      <td className="py-2 text-right">
        {/* Icon-only: a sixth column with a text label is what pushed the table
            past the viewport. The accessible name stays in the sr-only span. */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={(event) => {
            event.stopPropagation();
            sync.mutate();
          }}
          disabled={sync.isPending}
        >
          <RotateCcw className={sync.isPending ? "size-4 animate-spin" : "size-4"} />
          <span className="sr-only">Sync {product.reference}</span>
        </Button>
      </td>
    </tr>
    {expanded && (
      <tr className="border-b border-border last:border-0">
        <td colSpan={COLUMN_COUNT} className="pb-3">
          <ProductOffers productId={product.id} id={offersId} />
        </td>
      </tr>
    )}
    </>
  );
}

// Fetched when the row is first opened rather than with the page: 25 products a
// page, each with its own offers, is 25 queries nobody asked for.
function ProductOffers({ productId, id }: { productId: number; id: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["product-offers", productId],
    queryFn: () => api.productOffers(productId),
  });

  if (isLoading) {
    return (
      <div id={id} className="space-y-2 rounded-md bg-muted p-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p id={id} className="rounded-md bg-muted p-3 text-sm text-destructive">
        Could not load the offers
        {error instanceof Error ? `: ${error.message}` : "."}
      </p>
    );
  }

  const offers = data?.offers ?? [];
  if (offers.length === 0) {
    return (
      <p id={id} className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
        No supplier offers this product.
      </p>
    );
  }

  return (
    <div id={id} className="space-y-2 rounded-md bg-muted p-3">
      {/* Said out loud rather than silently dropping the ticks: an unmarked row
          would otherwise read as "not in Shopify", which is the one thing this
          column must never say without knowing. */}
      {data?.shopifyError !== null && data?.shopifyError !== undefined && (
        <p className="flex items-start gap-1.5 text-xs text-destructive">
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
          <span>
            Shopify could not be asked what it holds, so no offer is marked:{" "}
            {data.shopifyError}
          </span>
        </p>
      )}
      <table className="w-full caption-bottom text-xs">
        <caption className="sr-only">
          Supplier offers, best condition first, and within a condition the supplier
          the sync would pick
        </caption>
        <thead>
          <tr className="border-b border-border/60 text-left text-muted-foreground">
            <th scope="col" className="pb-1.5 pr-3 font-medium">Price ID</th>
            <th scope="col" className="pb-1.5 pr-3 font-medium">Supplier</th>
            <th scope="col" className="pb-1.5 pr-3 font-medium">Condition</th>
            <th scope="col" className="pb-1.5 pr-3 text-right font-medium">Cost</th>
            <th scope="col" className="pb-1.5 text-right font-medium">Sale price</th>
          </tr>
        </thead>
        <tbody>
          {offers.map((offer) => (
            <tr key={offer.priceId} className="border-b border-border/40 last:border-0">
              <td className="py-1.5 pr-3">
                <span className="flex items-center gap-1.5">
                  <span className="font-mono tabular-nums text-muted-foreground">
                    {offer.priceId}
                  </span>
                  {offer.inShopify === true && (
                    <>
                      <CheckCircle2
                        aria-hidden="true"
                        className="size-3.5 shrink-0 text-success"
                      />
                      <span className="sr-only">live in Shopify</span>
                    </>
                  )}
                </span>
              </td>
              <td className="py-1.5 pr-3">{offer.supplierName}</td>
              <td className="py-1.5 pr-3 text-muted-foreground">{offer.conditionName}</td>
              <td className="py-1.5 pr-3 text-right font-mono tabular-nums text-muted-foreground">
                {formatMoney(offer.cost, offer.currency)}
              </td>
              <td className="py-1.5 text-right font-mono tabular-nums text-foreground">
                {formatMoney(offer.salePrice, offer.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatMoney(amount: string, currency: string): string {
  return Number(amount).toLocaleString("en-GB", { style: "currency", currency });
}

// Why a product that is not in Shopify cannot get there yet. The one hard rule
// is the description: no description, no listing. A missing image is not one —
// the sync falls back to the default image rather than skipping the product —
// so it is reported next to this, not as a blocker.
function publishBlocker(product: CatalogueProduct): string | null {
  if (!product.hasDescription) return "no description";
  return null;
}
